import { memo, useCallback, useRef, useState } from "react";
import UnitRowsEditor from "./UnitRowsEditor";

/**
 * The units cell: one chip per supported unit, the base marked, the others
 * showing their multiplier into it — `kWh base` · `MWh ×1000`.
 *
 * The chips are a reading of the conversion table; the editing happens in the
 * panel they open, because a factor and a base radio do not fit a table cell.
 *
 * @param {object} props
 * @param {{ unitId: number, factor: number, isBase: boolean }[]} props.units
 * @param {{ id: number, unit: string, unit_name: string }[]} props.unitMaster
 * @param {boolean} [props.dirty]
 * @param {string} [props.error]
 * @param {boolean} [props.disabled]
 * @param {(action: { type: string, unitId?: number, factor?: number }) => void} props.onAction
 */
function UnitsCell({ units, unitMaster, dirty, error, disabled, onAction }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const cellRef = useRef(null);

  const labelFor = (unitId) => {
    const found = unitMaster.find((unit) => unit.id === unitId);
    return found ? found.unit : `unit ${unitId}`;
  };

  const classes = [
    "kgb-chipcell",
    dirty ? "kgb-chipcell--dirty" : "",
    error ? "kgb-chipcell--invalid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} ref={cellRef}>
      {units.map((unit) => (
        <span
          key={unit.unitId}
          className={`kgb-chip kgb-chip--unit${unit.isBase ? " kgb-chip--base" : ""}`}
          title={unit.isBase ? "Base unit" : `1 ${labelFor(unit.unitId)} = ${unit.factor} base units`}
        >
          <span>{labelFor(unit.unitId)}</span>
          <span className="kgb-chip-key">{unit.isBase ? "base" : `×${unit.factor}`}</span>
        </span>
      ))}

      {disabled ? null : (
        <button
          type="button"
          className="kgb-add-link"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          {units.length ? "Edit" : "+ Add units"}
        </button>
      )}

      {open ? (
        <UnitRowsEditor
          anchorRef={cellRef}
          units={units}
          unitMaster={unitMaster}
          error={error}
          onAdd={(unitId) => onAction({ type: "add", unitId })}
          onRemove={(unitId) => onAction({ type: "remove", unitId })}
          onSetBase={(unitId) => onAction({ type: "base", unitId })}
          onFactor={(unitId, factor) => onAction({ type: "factor", unitId, factor })}
          onClose={close}
        />
      ) : null}
    </div>
  );
}

export default memo(UnitsCell);
